import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useZendeskConnectRedirect from "@babackapp/private-api-client/client/use-zendesk-connect-redirect";
import useZendeskIntegrationUpdateMutation from "@babackapp/private-api-client/client/use-update-zendesk-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@babackapp/ui/components/input-group";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircleIcon } from "lucide-react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";

// Zendesk subdomain spec (mirrors backend SUBDOMAIN_PATTERN): lowercase
// alphanumeric with internal hyphens, max 63 chars.
const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

const formSchema = z.object({
  subDomain: z.string().min(1).regex(SUBDOMAIN_PATTERN),
});

// Users often paste the full Zendesk URL; strip protocol and the .zendesk.com
// suffix so we store only the bare subdomain the backend expects.
function sanitizeSubDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, "")
    .replace(/\.zendesk\.com\/?.*$/i, "")
    .replace(/\/.*$/, "");
}

const ZendeskIntegration = () => {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { subDomain, oauthConnected } = shop.integrations.zendesk;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { subDomain: subDomain ?? "" },
  });

  const { mutateAsync: updateZendeskIntegration } =
    useZendeskIntegrationUpdateMutation(shop);
  const connectRedirect = useZendeskConnectRedirect(shop);

  const onSubmit = form.handleSubmit((data) => {
    updateZendeskIntegration(data).then(() => form.reset(data));
  });

  // The subdomain must be saved before connecting: the backend reads it to
  // build the per-subdomain OAuth authorize URL.
  const canConnect = !!subDomain && !form.formState.isDirty;

  if (oauthConnected) {
    return (
      <div className="rounded-md bg-green-50 p-4 w-full">
        <div className="flex">
          <div className="flex-shrink-0">
            <CheckCircleIcon
              className="h-5 w-5 text-green-400"
              aria-hidden="true"
            />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              {t("settings.integrations.thirdpart.zendesk.connected")}
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <p>{subDomain}.zendesk.com</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="flex flex-col justify-start gap-8 mt-[16px]"
      >
        <FormField
          control={form.control}
          name="subDomain"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>
                {t("settings.integrations.thirdpart.zendesk.subDomain")}
              </FormLabel>
              <FormControl>
                <InputGroup>
                  <InputGroupInput
                    {...field}
                    onChange={(e) =>
                      field.onChange(sanitizeSubDomain(e.target.value))
                    }
                    placeholder="mysub"
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupText>.zendesk.com</InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex gap-4">
          <Button type="submit" disabled={!form.formState.isDirty}>
            {t("misc.save")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!canConnect}
            onClick={() => connectRedirect.mutateAsync()}
          >
            {t("settings.integrations.thirdpart.zendesk.connect")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export function ZendeskIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: updateActivation } =
    useZendeskIntegrationUpdateMutation(shop);

  const onActivationUpdate = useCallback(
    (activated: boolean) => {
      updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  if (!shop || !shop.plan.zendesk) return null;

  return (
    <IntegrationPreview
      title="Zendesk"
      logo="/integrations/zendesk.png"
      description={t("settings.integrations.thirdpart.zendesk.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.zendesk.activated}
      seeMoreUrl="https://zendesk.com"
    >
      <ZendeskIntegration />
    </IntegrationPreview>
  );
}
